import { BoardsCreateComponent } from '@main-application/boards/boards-create/boards-create.component';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { EIcon } from '@shared/enums/icon.enum';
import { LocalStorageDataEnum } from '@shared/enums/local-storage-data.enum';
import { PackageType } from '@shared/enums/package-type';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { MenuSection } from '@shared/interfaces/menu-config.interface';
import { NewTurnoverModalComponent } from '@ui-components/modals/new-turnover-modal/new-turnover-modal.component';

export const PROPUP_EMAIL_DOMAINS = ['gopropup.com'];
export const MANAGEMENT_EMAIL_DOMAINS = PROPUP_EMAIL_DOMAINS.concat(['onmicrosoft.com']);

/**
 * HOME SECTION
 * */
export const homeSection: MenuSection = {
  label: 'Home',
  urlPath: AppRoutes.HOME,
  icon: EIcon.HOME,
  hideWithoutSubsection: false,
  sectionItem: [],
};

/**
 * ROLES SECTION
 * */
export const rolesSection: MenuSection = {
  label: 'Home',
  urlPath: AppRoutes.ROLES,
  icon: EIcon.HOME,
  hideWithoutSubsection: true,
  packageLevel: [PackageType.Turns],
  sectionItem: [
    {
      label: 'Admin',
      urlPath: AppRoutes.ROLES_ADMIN_HOME,
      icon: EIcon.DASHBOARD,
      permissionLevel: PermissionLevelType.Administrator_Dashboard_Read,
    },
    {
      label: 'Asset',
      urlPath: AppRoutes.ROLES_ASSET_HOME,
      icon: EIcon.DASHBOARD2,
      permissionLevel: PermissionLevelType.AssetManager_PropertyDashboard_Read,
    },
    {
      label: 'Property',
      urlPath: AppRoutes.ROLES_PROPERTY_HOME,
      icon: EIcon.DASHBOARD4,
      permissionLevel: PermissionLevelType.PropertyManager_Dashboard_Read,
    },
    {
      label: 'Resident',
      urlPath: AppRoutes.RESIDENT_SERVICE,
      newTab: true,
      icon: EIcon.DASHBOARD5,
      emailDomains: PROPUP_EMAIL_DOMAINS,
      permissionLevel: [PermissionLevelType.Resident_Dashboard],
    },
    {
      label: 'Vendor',
      urlPath: AppRoutes.ROLES_VENDOR_HOME,
      permissionLevel: PermissionLevelType.Turnovers_Kanban_Read,
      forExternalOnly: true,
      icon: EIcon.TOOLS,
    },
  ],
};

/**
 * TURNOVERS SECTION
 * */
export const turnoversSection: MenuSection = {
  label: 'Turns',
  urlPath: AppRoutes.TURNOVERS,
  icon: EIcon.TIME_SCHEDULE,
  passQueryParams: true,
  permissionLevel: [
    PermissionLevelType.AllPermissions,
    PermissionLevelType.PropertyManager_ActiveTurnovers_Edit,
    PermissionLevelType.PropertyManager_ActiveTurnovers_Read,
    PermissionLevelType.Turnovers_Archived_Read,
  ],
  packageLevel: [PackageType.Turns],
  hideWithoutSubsection: false,
  sectionItem: [
    {
      label: 'Add turn',
      permissionLevelWarning: PermissionLevelType.PropertyManager_ActiveTurnovers_Edit,
      modalDialogComponent: NewTurnoverModalComponent,
    },
  ],
};

export const projectsSection: MenuSection = {
  label: 'Projects',
  urlPath: AppRoutes.PROJECTS,
  icon: EIcon.PROJECTS,
  passQueryParams: true,
  packageLevel: [PackageType.ProjectsPackageEnabled],
  permissionLevel: [PermissionLevelType.ProjectsList_View],
  hideWithoutSubsection: false,
};

export const boardsSection: MenuSection = {
  label: 'Boards',
  urlPath: AppRoutes.BOARDS,
  icon: EIcon.BOARD,
  passQueryParams: true,
  permissionLevel: [
    PermissionLevelType.AllPermissions,
    PermissionLevelType.Board_Use,
    PermissionLevelType.Board_Edit,
    PermissionLevelType.Board_View,
  ],
  packageLevel: [PackageType.Turns],
  hideWithoutSubsection: false,
  sectionItem: [
    {
      label: 'Add board',
      permissionLevelWarning: PermissionLevelType.Board_Edit,
      modalDialogComponent: BoardsCreateComponent,
    },
  ],
};

/**
 * INSPECTIONS SECTION
 * */
export const inspectionsSection: MenuSection = {
  label: 'Inspect',
  urlPath: AppRoutes.INSPECTIONS_USER,
  icon: EIcon.SEARCH,
  passQueryParams: true,
  hideWithoutSubsection: false,
  sectionItem: [
    {
      label: 'Standard',
      urlPath: AppRoutes.INSPECTIONS_USER,
      permissionLevel: PermissionLevelType.Inspections_List,
      icon: EIcon.SETTINGS2,
      sessionId: LocalStorageDataEnum.INSPECTION_FILTER_STANDARD_SETTINGS_ID,
    },
    {
      label: 'Resident',
      urlPath: AppRoutes.INSPECTIONS_RESIDENT,
      permissionLevel: PermissionLevelType.ResidentInspections_List,
      packageLevel: [PackageType.ResidentInspectionEnabled],
      icon: EIcon.SETTINGS2,
      sessionId: LocalStorageDataEnum.INSPECTION_FILTER_RESIDENT_SETTINGS_ID,
    },
    {
      permissionLevel: PermissionLevelType.Inspections_List,
      separator: true,
    },
    {
      label: 'Bulk',
      urlPath: AppRoutes.INSPECTIONS_BULK,
      permissionLevel: PermissionLevelType.Inspections_List,
      icon: EIcon.SETTINGS2,
    },
    {
      // do not display separators in case if there is no in review inspection
      permissionLevel: PermissionLevelType.Inspections_List,
      separator: true,
    },
    {
      label: 'In Review',
      urlPath: AppRoutes.INSPECTIONS_IN_REVIEW,
      permissionLevel: PermissionLevelType.Inspections_List,
      icon: EIcon.SETTINGS2,
    },
    {
      permissionLevel: PermissionLevelType.Inspections_List,
      separator: true,
    },
    {
      label: 'Dynamic',
      urlPath: AppRoutes.INSPECTIONS_DYNAMIC,
      permissionLevel: PermissionLevelType.Inspections_List,
      icon: EIcon.SETTINGS2,
    },
    {
      label: 'Repeating',
      urlPath: AppRoutes.INSPECTIONS_REPEATING,
      permissionLevel: PermissionLevelType.Inspections_List,
      icon: EIcon.SETTINGS2,
    },
    {
      label: 'Templates',
      urlPath: AppRoutes.SETTINGS_TEMPLATE,
      permissionLevel: PermissionLevelType.InspectionTemplates_List,
      icon: EIcon.SETTINGS2,
    },
  ],
  packageLevel: [PackageType.Turns, PackageType.Inspections],
};

export const calendarSection: MenuSection = {
  label: 'Calendar',
  urlPath: AppRoutes.CALENDAR,
  icon: EIcon.ICON_CALENDAR,
  permissionLevel: [PermissionLevelType.Board_View],
  packageLevel: [PackageType.Turns, PackageType.Inspections],
  hideWithoutSubsection: false,
  sectionItem: [],
};

export const serviceSection: MenuSection = {
  label: 'Service',
  urlPath: AppRoutes.SERVICE,
  icon: EIcon.Service,
  hideWithoutSubsection: false,
  sectionItem: [
    {
      label: 'Answers',
      urlPath: AppRoutes.SERVICE_CANNED_ANSWERS,
      permissionLevel: [],
    },
    {
      label: 'Parts Catalog',
      urlPath: AppRoutes.SPARE_PARTS,
      permissionLevel: [],
      packageLevel: [PackageType.SpareParts],
    },
  ],
  packageLevel: [PackageType.Turns, PackageType.Inspections, PackageType.Service, PackageType.SpareParts],
};

export const diligenceSection: MenuSection = {
  label: 'Diligence',
  urlPath: AppRoutes.DILIGENCE_LIST,
  icon: EIcon.ICON_DILIGENCE,
  permissionLevel: [PermissionLevelType.DueDiligence_Use, PermissionLevelType.DueDiligence_Edit],
  packageLevel: [PackageType.DiligencePackageEnabled],
  hideWithoutSubsection: false,
  sectionItem: [],
};

/**
 * REPORTS SECTION
 * */
export const reportsSection: MenuSection = {
  label: 'Reports',
  urlPath: AppRoutes.REPORTS,
  icon: EIcon.CHART_LINE,
  passQueryParams: true,
  hideWithoutSubsection: true,
  sectionItem: [
    {
      label: 'Rent Roll',
      urlPath: AppRoutes.REPORTS_RENT_ROLL,
      permissionLevel: [
        PermissionLevelType.PropertyManager_UnitList_Read,
        PermissionLevelType.PropertyManager_Dashboard_Read,
      ],
      icon: EIcon.DASHBOARD,
    },
    {
      label: 'ROC',
      urlPath: AppRoutes.REPORTS_ROC,
      permissionLevel: [
        PermissionLevelType.PropertyManager_UnitList_Read,
        PermissionLevelType.PropertyManager_Dashboard_Read,
      ],
      icon: EIcon.DASHBOARD,
    },
    {
      label: 'Billing',
      urlPath: AppRoutes.REPORTS_BILLING,
      permissionLevel: [PermissionLevelType.AllPermissions],
      emailDomains: MANAGEMENT_EMAIL_DOMAINS,
      icon: EIcon.DASHBOARD,
    },
    // {
    //   label: 'Unit Mix',
    //   urlPath: AppRoutes.REPORTS_UNIT_MIX,
    //   permissionLevel: [
    //     PermissionLevelType.PropertyManager_UnitMix_Read,
    //     PermissionLevelType.PropertyManager_Dashboard_Read,
    //   ],
    //   icon: EIcon.DASHBOARD2,
    // },
    // {
    //   label: 'Delinquencies',
    //   urlPath: AppRoutes.REPORTS_DELINQUENCIES,
    //   permissionLevel: [
    //     PermissionLevelType.PropertyManager_Delinquencies_Read,
    //     PermissionLevelType.PropertyManager_Dashboard_Read,
    //   ],
    //   icon: EIcon.THUNDER_CIRCLE,
    // },
    // {
    //   label: 'Renewals',
    //   urlPath: AppRoutes.REPORTS_RENEWALS,
    //   permissionLevel: [
    //     PermissionLevelType.PropertyManager_Renewals_Read,
    //     PermissionLevelType.PropertyManager_Dashboard_Read,
    //   ],
    //   icon: EIcon.REPEAT,
    // },
  ],
  packageLevel: [PackageType.ReportPackageEnabled],
};

/**
 * TICKET SECTION
 * */
export const ticketsSection: MenuSection = {
  label: 'Tickets',
  urlPath: AppRoutes.TICKETS,
  permissionLevel: PermissionLevelType.Tickets_Read,
  icon: EIcon.TICKET,
  hideWithoutSubsection: false,
  sectionItem: [],
};

/**
 * MANAGEMENT SECTION
 * */
export const managementSection: MenuSection = {
  label: 'Settings',
  urlPath: AppRoutes.SETTINGS,
  icon: EIcon.SETTINGS,
  hideWithoutSubsection: true,
  sectionItem: [
    // {
    //   label: 'KPIs',
    //   urlPath: AppRoutes.SETTINGS_KPI,
    //   permissionLevel: PermissionLevelType.Management_KPI_Read,
    //   icon: EIcon.DASHBOARD,
    // },
    {
      label: 'Portfolios',
      urlPath: AppRoutes.SETTINGS_PORTFOLIO,
      permissionLevel: PermissionLevelType.Management_Portfolio_Read,
      icon: EIcon.PORTFOLIOS,
    },
    {
      label: 'Property Groups',
      urlPath: AppRoutes.SETTINGS_COLLECTION,
      permissionLevel: [PermissionLevelType.PropertyCollections_Edit],
      icon: EIcon.STAR,
    },
    // {
    //   label: 'Renewals',
    //   urlPath: AppRoutes.SETTINGS_RENEWAL_STRATEGY,
    //   permissionLevel: PermissionLevelType.Management_UnitTargetRents_Read,
    //   icon: EIcon.DOLLAR,
    // },
    {
      label: 'Notification',
      urlPath: AppRoutes.SETTINGS_NOTIFICATIONS,
      icon: EIcon.NOTIFICATION,
    },
    {
      label: 'Residents',
      urlPath: AppRoutes.SETTINGS_RESIDENTS,
      permissionLevel: PermissionLevelType.Management_Residents,
      icon: EIcon.USER_GROUP,
    },
    {
      label: 'Roles',
      urlPath: AppRoutes.SETTINGS_ROLES,
      permissionLevel: [PermissionLevelType.Management_Roles],
      icon: EIcon.USER,
    },
    {
      label: 'Service',
      urlPath: AppRoutes.SETTINGS_SERVICE,
      icon: EIcon.Service,
    },
    {
      label: 'System',
      urlPath: AppRoutes.SETTINGS_SYSTEM,
      permissionLevel: PermissionLevelType.Chat_Hooks,
      icon: EIcon.SETTINGS2,
    },
    {
      label: 'Turns',
      urlPath: AppRoutes.SETTINGS_TURNOVER_CONFIGURATION,
      permissionLevel: PermissionLevelType.Management_TurnoverConfiguration_Read,
      icon: EIcon.TIME_SCHEDULE,
    },
    {
      label: 'Users',
      urlPath: AppRoutes.SETTINGS_USERS,
      permissionLevel: PermissionLevelType.Management_Users,
      icon: EIcon.USER_GROUP,
    },
    {
      label: 'Vendors',
      urlPath: AppRoutes.SETTINGS_VENDOR,
      permissionLevel: PermissionLevelType.Companies_Read,
      icon: EIcon.HALF_STAR,
    },
    {
      label: 'Inspect',
      urlPath: AppRoutes.SETTINGS_INSPECT,
      permissionLevel: PermissionLevelType.ManagementInspect,
      packageLevel: [PackageType.InspectionsPro],
      icon: EIcon.Inspect,
    },
    {
      label: 'Branding',
      urlPath: AppRoutes.SETTINGS_BRANDING,
      packageLevel: [PackageType.ResidentInspectionEnabled],
      permissionLevel: PermissionLevelType.BrandingSettings_View,
      icon: EIcon.Inspect,
    },
    {
      label: 'Property Model',
      urlPath: AppRoutes.SETTINGS_PROPERTY_MODEL,
      permissionLevel: [PermissionLevelType.Management_CustomFields_Edit],
      icon: EIcon.HOME,
    },
  ],
};
